<template>
  <main id="quote" class="page-container" v-if="$route.name === 'quotes'">
    <!-- Messaggi -->
    <!-- <p
      v-if="errorMessage"
      :class="{ 'error-message': true, show: errorMessage }"
    >
      {{ errorMessage }}
    </p>
    <p
      v-if="successMessage"
      :class="{ 'success-message': true, show: successMessage }"
    >
      {{ successMessage }}
    </p> -->

    <div class="table-container-quote">
      <!-- LOADER OVERLAY -->
      <div
        v-if="isLoading"
        class="loader-overlay"
        aria-live="polite"
        aria-busy="true"
      >
        <div class="spinner" role="status"></div>
        <p>Carico i preventivi…</p>
      </div>

      <!-- Toolbar -->
      <div class="quotes-toolbar">
        <!-- sc-47952: schede di stato mutuamente esclusive -->
        <div class="quote-tabs" role="tablist">
          <button
            v-for="t in quoteTabsDef"
            :key="t.slug"
            class="quote-tab"
            :class="{ 'quote-tab--active': activeTab === t.slug }"
            role="tab"
            :aria-selected="activeTab === t.slug"
            @click="switchTab(t.slug)"
          >
            {{ t.label }}
            <span class="quote-tab__count">{{ tabCounts[t.slug] ?? 0 }}</span>
          </button>
        </div>
        <router-link to="/home/quotes/add-quote" class="primary-btn">
          + Nuovo preventivo
        </router-link>
      </div>

      <!-- Tabella -->
      <div class="tbl-wrap" :class="{ dimmed: isLoading }">
        <table ref="fitTable" class="tbl quote-table">
          <thead>
            <tr>
              <th>Cliente</th>
              <th>Totale da pagare</th>
              <th>Indirizzo e data</th>
              <th>Stato conferma</th>
              <th>Stato pagamento</th>
              <th>Stato spedizione</th>
              <th>Documenti</th>
              <th></th>
            </tr>
          </thead>

          <tbody>
            <tr v-for="quote in allQuotes" :key="quote.id" class="quote-row">
              <!-- <td>{{ quote.Client.clientName }}</td> -->
              <td
                class="client-cell"
                @mouseenter="onCellEnter(quote.id)"
                @mouseleave="onCellLeave(quote.id)"
              >
                <button
                  class="client-trigger"
                  @focus="onCellEnter(quote.id)"
                  @blur="onCellLeave(quote.id)"
                  @click.stop.prevent="toggleTooltip(quote.id)"
                  @pointerdown="onTriggerPointer"
                  @keydown="onTriggerKeydown($event, quote.id)"
                  :aria-expanded="isTooltipVisible(quote.id)"
                  :aria-describedby="'tt-' + quote.id"
                  type="button"
                >
                  {{ quote.Client?.clientName || "—" }}
                  <span class="sr-only">Apri contatti</span>
                  <span class="client-hint-mobile">Tocca per contatti</span>
                </button>

                <transition name="fade">
                  <div
                    v-if="isTooltipVisible(quote.id)"
                    :id="'tt-' + quote.id"
                    role="tooltip"
                    class="email-tooltip"
                    :class="{ 'no-pointer': lockClickId === quote.id }"
                    @click.stop
                    @touchstart.stop
                  >
                    <!-- CONTENUTO TOOLTIP -->
                    <div class="tooltip-row" v-if="quote?.Client?.email">
                      <span class="label">Email</span>
                      <a :href="'mailto:' + quote.Client.email">{{
                        quote.Client.email
                      }}</a>
                      <button
                        class="copy-btn"
                        @click="copyToClipboard(quote.Client.email, 'email')"
                        title="Copia email"
                      >
                        <i class="fa-regular fa-copy"></i>
                      </button>
                    </div>

                    <div class="tooltip-row" v-if="quote?.Client?.pec">
                      <span class="label">PEC</span>
                      <a :href="'mailto:' + quote.Client.pec">{{
                        quote.Client.pec
                      }}</a>
                      <button
                        class="copy-btn"
                        @click="copyToClipboard(quote.Client.pec, 'pec')"
                        title="Copia PEC"
                      >
                        <i class="fa-regular fa-copy"></i>
                      </button>
                    </div>

                    <div class="tooltip-row" v-if="quote?.Client?.phoneNumber">
                      <span class="label">Telefono</span>
                      {{ quote.Client.phoneNumber }}
                      <button
                        class="copy-btn"
                        @click="
                          copyToClipboard(quote.Client.phoneNumber, 'phone')
                        "
                        title="Copia numero di telefono"
                      >
                        <i class="fa-regular fa-copy"></i>
                      </button>
                    </div>

                    <div
                      class="tooltip-row muted"
                      v-if="
                        !quote?.Client?.email &&
                        !quote?.Client?.pec &&
                        !quote?.Client?.phoneNumber
                      "
                    >
                      Nessun contatto disponibile.
                    </div>
                  </div>
                </transition>
              </td>

              <td>
                <!-- Pagato più del dovuto: un credito, non un importo negativo (sc-48721) -->
                <span
                  v-if="daRestituire(quote.realTotalPrice) > 0"
                  class="da-restituire"
                  title="Il cliente ha pagato più del totale"
                >
                  Da restituire
                  {{ formatPriceToPrint(daRestituire(quote.realTotalPrice)) }}
                </span>
                <template v-else>
                  {{ formatPriceToPrint(quote.realTotalPrice) }}
                </template>
              </td>
              <!-- [sc-49210] Indirizzo di consegna e, sotto, data del preventivo -->
              <td class="address-date-cell">
                <a
                  v-if="quote.location && quote.location.link"
                  :href="quote.location.link"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="custom-link address-date-cell__address"
                  :title="quote.location.address || 'Indirizzo non disponibile'"
                >
                  {{ quote.location.address || "Indirizzo non disponibile" }}
                </a>
                <span
                  v-else-if="quote.location && quote.location.address"
                  class="address-date-cell__address"
                  :title="quote.location.address"
                  >{{ quote.location.address }}</span
                >
                <span v-else class="address-date-cell__address"
                  >Indirizzo non disponibile</span
                >
                <span class="address-date-cell__date">{{
                  formatToLocalDate(quote.date)
                }}</span>
              </td>

              <!-- Conferma -->
              <td class="state-container">
                <div class="status-wrapper">
                  <AppSelect
                    v-model="quote.isConfirmed"
                    :disabled="Boolean(quote.isConfirmed) === true"
                    @change="
                      onQuoteStateChange(
                        quote,
                        'isConfirmed',
                        quote.isConfirmed
                      )
                    "
                    :options="confirmOptions"
                  />
                </div>
              </td>

              <!-- Pagamento -->
              <td class="state-container">
                <div class="status-wrapper">
                  <AppSelect
                    v-model="quote.payment_state"
                    :disabled="
                      quote.payment_state === 'Pagato' ||
                      Boolean(quote.isConfirmed) === false
                    "
                    @change="
                      onQuoteStateChange(
                        quote,
                        'payment_state',
                        quote.payment_state
                      )
                    "
                    :options="paymentOptions"
                  />
                </div>
              </td>

              <!-- Spedizione -->
              <td class="state-container">
                <div class="status-wrapper">
                  <AppSelect
                    v-model="quote.state"
                    :disabled="
                      quote.state === 'Consegnato' ||
                      Boolean(quote.isConfirmed) === false
                    "
                    @change="onQuoteStateChange(quote, 'state', quote.state)"
                    :options="shippingOptions"
                  />
                </div>
              </td>

              <!-- Documenti -->
              <td>
                <!-- [sc-49210] Icona PDF ufficiale (Adobe), piccola, con etichetta -->
                <button
                  type="button"
                  class="pdf-chip"
                  @click="openDocumentsModal(quote)"
                  title="Apri i documenti del preventivo"
                  aria-label="Apri i documenti del preventivo"
                >
                  <img
                    class="pdf-chip__icon"
                    src="@/assets/pdf-svg.svg"
                    alt=""
                    aria-hidden="true"
                  />
                  <span class="pdf-chip__label">PDF</span>
                </button>
              </td>

              <!-- Azioni -->

              <td class="actions-cell">
                <button
                  v-if="canEdit(quote) || !isArchivedTab"
                  class="icons-button"
                  @click="openDiscountModal(quote)"
                  :disabled="preventivoNonModificabile(quote)"
                  :title="
                    preventivoNonModificabile(quote)
                      ? 'Preventivo pagato e in consegna: non modificabile'
                      : 'Applica sconto o acconto'
                  "
                  aria-label="Applica sconto o acconto"
                >
                  <i class="fa-solid fa-euro-sign"></i>
                </button>

                <button
                  class="icons-button"
                  @click="goToQuoteDetail(quote.id)"
                  title="Dettaglio preventivo"
                  aria-label="Dettaglio preventivo"
                >
                  <i class="fa-solid fa-eye"></i>
                </button>

                <button
                  v-if="hasQuoteDocument(quote)"
                  :class="[
                    'icons-button',
                    { 'icons-button--email-sent': hasSentQuoteEmail(quote) },
                  ]"
                  @click="sendExistingQuoteEmail(quote)"
                  :disabled="sendingQuoteEmailId === quote.id"
                  :title="
                    sendingQuoteEmailId === quote.id
                      ? 'Invio email in corso'
                      : 'Invia il PDF gia caricato al cliente'
                  "
                  aria-label="Invia il PDF gia caricato al cliente"
                >
                  <i class="fa-solid fa-envelope"></i>
                </button>

                <button
                  v-if="!isArchivedTab"
                  class="icons-button"
                  @click="openArchiveModal(quote)"
                  title="Archivia preventivo"
                  aria-label="Archivia preventivo"
                >
                  <i class="fa-solid fa-trash-can"></i>
                </button>

                <button
                  v-else-if="canRestore(quote)"
                  class="icons-button icons-button--restore"
                  @click="openRestoreModal(quote)"
                  title="Ripristina preventivo"
                  aria-label="Ripristina preventivo"
                >
                  <i class="fa-solid fa-trash-arrow-up"></i>
                </button>

                <!-- sc-48744: elimina dalla UI, la riga resta a database -->
                <button
                  v-if="isArchivedTab"
                  class="icons-button icons-button--delete"
                  @click="openDeleteModal(quote)"
                  title="Elimina preventivo dalle liste"
                  aria-label="Elimina preventivo dalle liste"
                >
                  <i class="fa-solid fa-xmark"></i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination / search / archived toggle -->
      <div class="pagination">
        <div class="search-container">
          <input
            type="text"
            v-model="searchTerm"
            @keyup.enter="onSearch"
            placeholder="Cerca preventivi..."
            class="search-box"
            :disabled="isLoading"
          />

          <button
            v-if="searchTerm"
            @click="onResetSearch"
            class="clear-search-button"
            :disabled="isLoading"
          >
            <i class="fa-solid fa-times"></i>
          </button>
        </div>

        <div class="pagination-info">
          {{ pagination.startItem }}-{{ pagination.endItem }} di
          {{ pagination.totalItems }}
        </div>

        <div class="pagination-controls">
          <button
            class="icons-button"
            @click="goToPage(1)"
            :disabled="isLoading || pagination.currentPage === 1"
            :title="
              isLoading || pagination.currentPage === 1
                ? null
                : 'Torna alla prima pagina'
            "
          >
            <i class="fa-solid fa-angles-left"></i>
          </button>
          <button
            class="icons-button"
            @click="goToPage(pagination.currentPage - 1)"
            :disabled="isLoading || pagination.currentPage === 1"
            :title="
              isLoading || pagination.currentPage === 1
                ? null
                : 'Torna alla pagina precedente'
            "
          >
            <i class="fa-solid fa-chevron-left"></i>
          </button>

          <button
            v-for="pageNum in visiblePages"
            :key="pageNum"
            @click="goToPage(pageNum)"
            :class="{ active: pagination.currentPage === pageNum }"
            class="pages-button"
          >
            {{ pageNum }}
          </button>

          <button
            class="icons-button"
            @click="goToPage(pagination.currentPage + 1)"
            :disabled="
              isLoading || pagination.currentPage === pagination.totalPages
            "
            :title="
              isLoading || pagination.currentPage === pagination.totalPages
                ? null
                : 'Vai alla pagina successiva'
            "
          >
            <i class="fa-solid fa-chevron-right"></i>
          </button>
          <button
            class="icons-button"
            @click="goToPage(pagination.totalPages)"
            :disabled="
              isLoading || pagination.currentPage === pagination.totalPages
            "
            :title="
              isLoading || pagination.currentPage === pagination.totalPages
                ? null
                : `Vai all'ultima pagina`
            "
          >
            <i class="fa-solid fa-angles-right"></i>
          </button>
        </div>
      </div>

      <!-- MODALE DOCUMENTI -->
      <VueFinalModal
        v-model="showDocumentsModal"
        :clickToClose="true"
        :escToClose="true"
        classes="documents-modal"
        content-class="documents-modal-content quote-docs-modal"
      >
        <template #default>
          <button
            class="modal__close icons-button"
            @click="closeDocumentsModal"
          >
            <i class="fa-solid fa-xmark"></i>
          </button>
          <h3 class="modal__title">Preventivo #{{ currentQuoteId }}</h3>

          <!-- card rigenerazione PDF: rigenera lato client e riscarica -->
          <button
            type="button"
            class="regenerate-pdf-card"
            :disabled="regeneratingPdf"
            @click="regenerateQuotePdf"
          >
            <span class="regenerate-pdf-card__icon">
              <i
                :class="
                  regeneratingPdf
                    ? 'fa-solid fa-spinner fa-spin'
                    : 'fa-solid fa-arrows-rotate'
                "
              ></i>
            </span>
            <span class="regenerate-pdf-card__body">
              <span class="regenerate-pdf-card__title">
                {{
                  regeneratingPdf ? "Rigenerazione in corso…" : "Rigenera PDF"
                }}
              </span>
              <span class="regenerate-pdf-card__subtitle">
                Aggiorna il documento e scaricalo
              </span>
            </span>
          </button>

          <!-- mostra il riferimento al pdf associato al preventivo -->
          <transition-group name="card" tag="div" class="documents-grid">
            <div
              v-for="(doc, i) in filteredDocuments"
              :key="safeDocKey(doc, i)"
              class="document-card"
            >
              <a href="#" class="card-link" @click.prevent="openDoc(doc)">
                <div class="card-icon-pdf">
                  <i class="fa-solid fa-file-pdf"></i>
                </div>
                <div class="card-body">
                  <div class="card-title">{{ formatDocName(doc.name) }}</div>
                  <div class="card-subtitle">
                    Data di creazione: {{ formatToLocalDate(doc.createdAt) }}
                  </div>
                </div>
              </a>
            </div>

            <div v-if="filteredDocuments.length === 0" class="no-result">
              Nessun documento trovato
            </div>
          </transition-group>
        </template>
      </VueFinalModal>

      <!-- MODALE ARCHIVIAZIONE -->
      <VueFinalModal
        v-model="showArchiveModal"
        :clickToClose="!archiving"
        :escToClose="!archiving"
        classes="documents-modal"
        content-class="confirm-modal-content modal--archive"
      >
        <template #default>
          <button
            class="modal__close icons-button"
            @click="closeArchiveModal"
            :disabled="archiving"
            title="Chiudi"
          >
            <i class="fa-solid fa-xmark"></i>
          </button>

          <h3 class="modal__title">Archivia preventivo</h3>
          <p class="modal__subtitle">
            Archiviare il preventivo
            <strong>#{{ currentQuoteId }}</strong>
            di
            <strong>{{ selectedQuote?.Client?.clientName }}</strong
            >?
          </p>

          <div class="modal-actions">
            <button
              class="secondary-btn"
              @click="closeArchiveModal"
              :disabled="archiving"
            >
              Annulla
            </button>
            <button
              class="primary-btn confirm-btn"
              @click="confirmArchive"
              :disabled="archiving"
            >
              <span v-if="archiving">Archivia…</span>
              <span v-else>Archivia</span>
            </button>
          </div>
        </template>
      </VueFinalModal>

      <!-- MODALE RIPRISTINO -->
      <VueFinalModal
        v-model="showRestoreModal"
        :clickToClose="!restoring"
        :escToClose="!restoring"
        classes="documents-modal"
        content-class="confirm-modal-content modal--restore"
      >
        <template #default>
          <button
            class="modal__close icons-button"
            @click="closeRestoreModal"
            :disabled="restoring"
            title="Chiudi"
            aria-label="Chiudi"
          >
            <i class="fa-solid fa-xmark"></i>
          </button>

          <h3 class="modal__title">Ripristina preventivo</h3>
          <p class="modal__subtitle">
            Ripristinare il preventivo
            <strong>#{{ currentQuoteId }}</strong>
            di
            <strong>{{ selectedQuote?.Client?.clientName }}</strong> dagli
            archivi?
          </p>

          <div class="modal-actions">
            <button
              class="secondary-btn"
              @click="closeRestoreModal"
              :disabled="restoring"
            >
              Annulla
            </button>
            <button
              class="primary-btn confirm-btn"
              @click="confirmRestore"
              :disabled="restoring"
            >
              <span v-if="restoring">Ripristino…</span>
              <span v-else>Ripristina</span>
            </button>
          </div>
        </template>
      </VueFinalModal>

      <!-- MODALE CONFERMA ELIMINAZIONE (sc-48744) -->
      <VueFinalModal
        v-model="showDeleteModal"
        :clickToClose="!deleting"
        :escToClose="!deleting"
        classes="documents-modal"
        content-class="confirm-modal-content modal--restore"
      >
        <template #default>
          <button
            class="modal__close icons-button"
            @click="closeDeleteModal"
            :disabled="deleting"
            title="Chiudi"
            aria-label="Chiudi"
          >
            <i class="fa-solid fa-xmark"></i>
          </button>

          <h3 class="modal__title">Elimina preventivo</h3>
          <p class="modal__subtitle">
            Eliminare il preventivo
            <strong>#{{ currentQuoteId }}</strong>
            di
            <strong>{{ selectedQuote?.Client?.clientName }}</strong
            >? Sparirà dalle liste, dalla ricerca articoli e non sarà più
            raggiungibile dalla piattaforma.
          </p>
          <p class="modal__subtitle modal__subtitle--warning">
            I dati restano salvati, ma
            <strong>non puoi reinserirlo da solo</strong>: per rimetterlo nelle
            liste devi scrivere a <strong>Liberotratto</strong> indicando il
            numero del preventivo.
          </p>

          <div class="modal-actions">
            <button
              class="secondary-btn"
              @click="closeDeleteModal"
              :disabled="deleting"
            >
              Annulla
            </button>
            <button
              class="primary-btn confirm-btn"
              @click="confirmDelete"
              :disabled="deleting"
            >
              <span v-if="deleting">Elimino…</span>
              <span v-else>Elimina</span>
            </button>
          </div>
        </template>
      </VueFinalModal>

      <!-- MODALE CONFERMA CAMBIO STATO -->
      <VueFinalModal
        v-model="showConfirmModal"
        :clickToClose="!confirmBusy"
        :escToClose="!confirmBusy"
        classes="documents-modal"
        content-class="confirm-modal-content modal--state"
      >
        <template #default>
          <button
            class="modal__close icons-button"
            @click="handleConfirmCancel"
            :disabled="confirmBusy"
            title="Chiudi"
          >
            <i class="fa-solid fa-xmark"></i>
          </button>

          <h3 class="modal__title">{{ confirmTitle }}</h3>
          <p class="modal__subtitle" v-html="confirmMessage"></p>

          <div class="modal-actions">
            <button
              class="secondary-btn"
              @click="handleConfirmCancel"
              :disabled="confirmBusy"
            >
              Annulla
            </button>
            <button
              class="primary-btn confirm-btn"
              @click="handleConfirmOK"
              :disabled="confirmBusy"
            >
              <span v-if="confirmBusy">{{ confirmBusyLabel }}</span>
              <span v-else>{{ confirmLabel }}</span>
            </button>
          </div>
        </template>
      </VueFinalModal>

      <!-- MODALE INSERIMENTO ACCONTO O SCONTO -->
      <VueFinalModal
        v-model="showDiscountModal"
        :clickToClose="true"
        :escToClose="true"
        classes="documents-modal"
        content-class="documents-modal-content"
      >
        <template #default>
          <button class="modal__close icons-button" @click="closeDiscountModal">
            <i class="fa-solid fa-xmark"></i>
          </button>

          <h3 class="modal__title">Preventivo #{{ currentQuoteId }}</h3>

          <transition-group name="card" tag="div" class="documents-grid">
            <div
              v-for="(quote, i) in allQuotes.filter(
                (q) => q.id === currentQuoteId
              )"
              :key="safeDocKey(quote, i)"
              class="quote-modifier"
            >
              <!-- Modificatori già presenti -->
              <div
                class="current-modifiers"
                v-if="
                  (quote.discount_percent ?? 0) > 0 || (quote.deposit ?? 0) > 0
                "
              >
                <p v-if="(quote.discount_percent ?? 0) > 0">
                  <strong>Sconto applicato: </strong>
                  {{ Number(quote.discount_percent).toFixed(2) }}%
                  <span v-if="(quote.discount ?? 0) > 0">
                    ({{ formatPriceToPrint(quote.discount) }})
                  </span>
                </p>
                <p v-if="(quote.deposit ?? 0) > 0">
                  <strong>Acconto applicato: </strong>
                  <span v-if="(quote.deposit ?? 0) > 0">
                    {{ formatPriceToPrint(quote.deposit) }}
                  </span>
                </p>
                <p class="hint">Vuoi inserire un ulteriore sconto o acconto?</p>
              </div>

              <!-- Prezzo totale da saldare (al netto di sconto/acconto già applicati) -->
              <p class="quote-total">
                <strong>Totale da saldare: </strong>
                {{ formatPriceToPrint(residualToPay(quote)) }}
              </p>

              <!-- Form applicazione -->
              <div class="modifier-form">
                <label>
                  <input
                    type="radio"
                    value="discount_percent"
                    v-model="modifierType"
                  />
                  <strong>Sconto (€)</strong>
                </label>
                <label>
                  <input type="radio" value="deposit" v-model="modifierType" />
                  <strong>Acconto (€)</strong>
                </label>

                <!-- input importo sconto -->
                <input
                  v-if="modifierType === 'discount_percent'"
                  type="number"
                  v-model.number="discountAmount"
                  min="0"
                  :max="
                    Math.max(
                      0,
                      (Number(quote.totalPrice) || 0) -
                        (Number(quote.deposit) || 0)
                    )
                  "
                  step="1"
                  placeholder="0.00"
                  @input="clampDiscountAmount(quote)"
                />

                <!-- input acconto -->
                <input
                  v-else-if="modifierType === 'deposit'"
                  type="number"
                  v-model.number="depositAmount"
                  min="0"
                  :max="
                    Math.max(
                      0,
                      (Number(quote.totalPrice) || 0) -
                        (Number(quote.discount) || 0)
                    )
                  "
                  step="1"
                  placeholder="0.00"
                  @input="clampDepositAmount(quote)"
                />

                <button
                  class="primary-btn confirm-btn"
                  :disabled="confirmBusy || !canApplyModifier"
                  @click="applyModifier(quote)"
                >
                  <span v-if="confirmBusy">Applica…</span>
                  <span v-else>Applica</span>
                </button>
              </div>

              <!-- Preview dinamica -->
              <div class="preview" v-if="modifierType">
                <!-- Caso 1: Sto applicando uno SCONTO % (considera l'eventuale acconto già applicato) -->
                <div
                  v-if="
                    modifierType === 'discount_percent' && discountAmount > 0
                  "
                  class="breakdown"
                >
                  <div class="title">Anteprima sconto su parziale</div>
                  <ul class="calc-list">
                    <li>
                      Totale preventivo:
                      <strong>{{
                        formatPriceToPrint(Number(quote.totalPrice) || 0)
                      }}</strong>
                    </li>
                    <li v-if="(Number(quote.deposit) || 0) > 0">
                      Acconto già applicato:
                      <strong
                        >-
                        {{
                          formatPriceToPrint(Number(quote.deposit) || 0)
                        }}</strong
                      >
                    </li>
                    <li v-if="(Number(quote.deposit) || 0) > 0">
                      Parziale A (totale − acconto):
                      <span class="calc-emph">
                        <strong>
                          {{
                            formatPriceToPrint(
                              Math.max(
                                0,
                                (Number(quote.totalPrice) || 0) -
                                  (Number(quote.deposit) || 0)
                              )
                            )
                          }}
                        </strong>
                      </span>
                    </li>
                    <li>
                      Sconto da applicare:
                      <strong>
                        {{
                          formatPriceToPrint(
                            Math.min(
                              Math.max(0, Number(discountAmount) || 0),
                              Math.max(
                                0,
                                (Number(quote.totalPrice) || 0) -
                                  (Number(quote.deposit) || 0)
                              )
                            )
                          )
                        }}
                      </strong>
                    </li>
                    <li class="row-highlight">
                      Nuovo totale stimato:
                      <span class="calc-final">
                        <strong>
                          {{
                            formatPriceToPrint(
                              Math.max(
                                0,
                                Math.max(
                                  0,
                                  (Number(quote.totalPrice) || 0) -
                                    (Number(quote.deposit) || 0)
                                ) - (Number(discountAmount) || 0)
                              )
                            )
                          }}
                        </strong>
                      </span>
                    </li>
                  </ul>
                </div>

                <!-- Caso 2: Sto applicando un ACCONTO € (considera l'eventuale sconto già applicato) -->
                <div
                  v-else-if="modifierType === 'deposit' && depositAmount > 0"
                  class="breakdown"
                >
                  <div class="title">Anteprima acconto su parziale</div>
                  <ul class="calc-list">
                    <li>
                      Totale preventivo:
                      <strong>{{
                        formatPriceToPrint(Number(quote.totalPrice) || 0)
                      }}</strong>
                    </li>
                    <li
                      v-if="
                        (Number(quote.discount_percent) || 0) > 0 ||
                        (Number(quote.discount) || 0) > 0
                      "
                    >
                      Sconto già applicato
                      <template
                        v-if="(Number(quote.discount_percent) || 0) > 0"
                      >
                        ({{
                          Number(quote.discount_percent).toFixed(2)
                        }}%) </template
                      >:
                      <strong>
                        {{
                          formatPriceToPrint(
                            // usa l'importo sconto se presente, altrimenti calcolo da percentuale
                            Number(quote.discount) ||
                              ((Number(quote.totalPrice) || 0) *
                                (Number(quote.discount_percent) || 0)) /
                                100
                          )
                        }}
                      </strong>
                    </li>
                    <li
                      v-if="
                        (Number(quote.discount_percent) || 0) > 0 ||
                        (Number(quote.discount) || 0) > 0
                      "
                    >
                      Parziale A (totale − sconto):
                      <span class="calc-emph">
                        <strong>
                          {{
                            formatPriceToPrint(
                              Math.max(
                                0,
                                (Number(quote.totalPrice) || 0) -
                                  (Number(quote.discount) ||
                                    ((Number(quote.totalPrice) || 0) *
                                      (Number(quote.discount_percent) || 0)) /
                                      100)
                              )
                            )
                          }}
                        </strong>
                      </span>
                    </li>
                    <li>
                      Acconto da applicare:
                      <strong>
                        {{
                          formatPriceToPrint(
                            Math.max(0, Number(depositAmount) || 0)
                          )
                        }}</strong
                      >
                    </li>
                    <li class="row-highlight">
                      Nuovo totale stimato:
                      <span class="calc-final">
                        <strong>
                          {{
                            formatPriceToPrint(
                              Math.max(
                                0,
                                Math.max(
                                  0,
                                  (Number(quote.totalPrice) || 0) -
                                    (Number(quote.discount) ||
                                      ((Number(quote.totalPrice) || 0) *
                                        (Number(quote.discount_percent) || 0)) /
                                        100)
                                ) - (Number(depositAmount) || 0)
                              )
                            )
                          }}
                        </strong>
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <!-- (resto del grid dei documenti, invariato) -->
            <div
              v-for="(doc, i) in filteredDocuments"
              :key="safeDocKey(doc, i)"
              class="document-card"
            >
              <a href="#" class="card-link" @click.prevent="openDoc(doc)">
                <div class="card-icon-pdf">
                  <i class="fa-solid fa-file-pdf"></i>
                </div>
                <div class="card-body">
                  <div class="card-title">{{ formatDocName(doc.name) }}</div>
                  <div class="card-subtitle">
                    Data di creazione: {{ formatToLocalDate(doc.createdAt) }}
                  </div>
                </div>
              </a>
            </div>
            <div v-if="filteredDocuments.length === 0" class="no-result">
              Nessun documento trovato
            </div>
          </transition-group>
        </template>
      </VueFinalModal>
    </div>
  </main>
  <router-view></router-view>
</template>

<script>
import logoPath from "@/assets/emmamaison.png";
import axiosInstance from "@/axiosInstance";
import { openDocument } from "@/utilities/openDocument";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { mapActions, mapGetters } from "vuex/dist/vuex.cjs.js";
import {
  formatPriceToPrint,
  formatToLocalDate,
  formatToLocalDateAndHours,
} from "../utilities/formatting";
import { itemFullDescription } from "../utilities/itemDescription";
import { pdfSafe, pdfSafeRows } from "../utilities/pdfText";
import {
  calcolaImporti,
  disegnaRiquadroRiepilogo,
  righeRiepilogo,
  totaliImponibile,
} from "../utilities/pdfRiepilogo";
import {
  daRestituire,
  preventivoNonModificabile,
} from "../utilities/pagamenti";
import { fitPageSizeMixin } from "@/utilities/fitPageSize";

export default {
  // [sc-49210] elementi per pagina calcolati dallo schermo (pageSize)
  mixins: [fitPageSizeMixin],
  computed: {
    ...mapGetters({
      allQuotes: "quotes/list",
      pagination: "quotes/pagination",
      tabCounts: "quotes/tabCounts",
    }),

    visiblePages() {
      const range = 2;
      const start = Math.max(1, this.pagination.currentPage - range);
      const end = Math.min(
        this.pagination.totalPages,
        this.pagination.currentPage + range
      );
      return Array.from({ length: end - start + 1 }, (_, i) => start + i);
    },

    // sc-47952: scheda archiviati = preventivi col cestino (isArchived).
    isArchivedTab() {
      return this.activeTab === "archiviati";
    },
    quoteTabsDef() {
      return [
        { slug: "da-confermare", label: "Da confermare" },
        { slug: "confermati", label: "Confermati" },
        { slug: "parziali", label: "Pagati o Consegnati" },
        { slug: "completati", label: "Completati" },
        { slug: "archiviati", label: "Archiviati" },
      ];
    },

    filteredDocuments() {
      const list = Array.isArray(this.selectedDocuments)
        ? this.selectedDocuments
        : [];
      const term = (this.docSearchTerm || "").toLowerCase().trim();
      const seen = new Set();
      return list.filter(Boolean).filter((doc) => {
        const k = doc.id ?? doc.name ?? doc.url ?? Math.random();
        if (seen.has(k)) return false;
        seen.add(k);
        return term ? (doc.name || "").toLowerCase().includes(term) : true;
      });
    },
    canApplyModifier() {
      if (this.modifierType === "discount_percent") {
        const base = Math.max(
          0,
          (Number(this.selectedQuote?.totalPrice) || 0) -
            (Number(this.selectedQuote?.deposit) || 0)
        );
        return this.discountAmount > 0 && this.discountAmount <= base;
      }
      if (this.modifierType === "deposit") {
        const base = Math.max(
          0,
          (Number(this.selectedQuote?.totalPrice) || 0) -
            (Number(this.selectedQuote?.discount) || 0)
        );
        return this.depositAmount > 0 && this.depositAmount <= base;
      }
      return false;
    },
  },

  data() {
    return {
      // [sc-49210] Opzioni dei menu di stato: i colori sono quelli dei pallini
      confirmOptions: [
        { value: false, label: "Pendente", color: "#ffa726" },
        { value: true, label: "Confermato", color: "#4caf50" },
      ],
      paymentOptions: [
        { value: "In attesa", label: "In attesa", color: "#ffa726" },
        { value: "Pagato", label: "Pagato", color: "#4caf50" },
      ],
      shippingOptions: [
        { value: "In consegna", label: "In consegna", color: "#ffa726" },
        { value: "Consegnato", label: "Consegnato", color: "#4caf50" },
      ],
      tooltipOpenId: null,
      hoveredId: null,
      hideTimer: null,
      lockClickId: null, // anti-ghost click
      // [mobile] dopo un tocco l'hover non conta: su touch non arriva mai il
      // mouseleave che azzera hoveredId e il riquadro restava aperto
      isTouch: false,

      isLoading: false,
      errorMessage: "",
      successMessage: "",
      currentPage: 1,
      searchTerm: "",
      currentQuoteId: null,
      showDocumentsModal: false,
      selectedDocuments: [],
      regeneratingPdf: false, // busy della card "Rigenera PDF" nella modale documenti
      activeTab: "da-confermare", // sc-47952: scheda attiva
      depositAmount: 0,
      modifierType: "",
      discountAmount: 0,
      docSearchTerm: "", // per ricerca documenti nella modale

      /* Modali archiviazione / ripristino */
      showArchiveModal: false,
      archiving: false,
      showRestoreModal: false,
      showDeleteModal: false, // sc-48744
      deleting: false,
      restoring: false,
      selectedQuote: null,

      showConfirmModal: false,
      showDiscountModal: false,
      confirmTitle: "",
      confirmMessage: "",
      confirmLabel: "Conferma",
      confirmBusyLabel: "Aggiorno…",
      confirmBusy: false,
      sendingQuoteEmailId: null,
      pendingChange: null, // { id, field, newValue }
    };
  },

  methods: {
    async openDoc(doc) {
      try {
        await openDocument(doc);
      } catch (err) {
        console.error("Errore nell'apertura del documento:", err);
        this.$toast.error("Impossibile aprire il documento");
      }
    },
    // [sc-49210] il numero di righe che stanno nello schermo è cambiato
    onPageSizeChange() {
      this.currentPage = 1;
      return this.getQuotes(1);
    },
    ...mapActions([
      "fetchQuotes",
      "archiveQuote",
      "restoreQuote",
      "deleteQuote",
      "uploadQuoteDocument",
      "sendQuoteEmail",
    ]),

    isTooltipVisible(id) {
      return this.tooltipOpenId === id || this.hoveredId === id;
    },
    applyTooltipPlacement(id) {
      const tip = document.getElementById("tt-" + id);
      if (tip) tip.dataset.placement = "over";
    },
    closeTooltip() {
      this.tooltipOpenId = null;
      this.hoveredId = null;
    },
    onCellEnter(id) {
      if (this.isTouch) return;
      clearTimeout(this.hideTimer);
      this.hoveredId = id;
      this.$nextTick(() => this.applyTooltipPlacement(id));
    },
    onCellLeave(id) {
      clearTimeout(this.hideTimer);
      // piccolo delay per evitare micro-sfarfallii
      this.hideTimer = setTimeout(() => {
        // se non è aperto "sticky" con click, chiudi l'hover
        if (this.tooltipOpenId !== id) this.hoveredId = null;
      }, 120);
    },
    toggleTooltip(id) {
      if (this.tooltipOpenId === id) {
        this.closeTooltip();
        return;
      }
      this.tooltipOpenId = id;
      this.hoveredId = id;
      this.lockClickId = id; // blocca click subito dopo l’apertura
      setTimeout(() => {
        if (this.lockClickId === id) this.lockClickId = null;
      }, 250); // ~250ms bastano per il ghost click
      this.$nextTick(() => this.applyTooltipPlacement(id));
    },

    onTriggerPointer(e) {
      // [mobile] il tocco apre/chiude dal normale click: qui si registra solo
      // che è un dito, senza preventDefault (Chrome lo ignora sui touchstart
      // non annullabili e segnala "[Intervention]" in console)
      if (e.pointerType === "touch") this.isTouch = true;
    },
    onTriggerKeydown(e, id) {
      if (e.key === "Escape") {
        this.closeTooltip();
      } else if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        this.toggleTooltip(id);
      }
    },
    // se vuoi mantenere queste API:
    showTooltip(id) {
      this.onCellEnter(id);
    },
    hideTooltip() {
      // opzionale: se chiamata da blur del bottone
      const id = this.tooltipOpenId ?? this.hoveredId;
      this.onCellLeave(id);
    },
    handleDocumentClick(e) {
      // chiudi se clicco fuori da qualsiasi .client-cell
      if (!e.target.closest(".client-cell")) {
        this.closeTooltip();
      }
    },
    async copyToClipboard(text, kind = null) {
      const value = String(text ?? "").trim();

      // Se il tipo non è passato, prova a inferirlo
      if (!kind) {
        if (/^\+?[\d()\s.-]{5,}$/.test(value)) kind = "phone";
        else if (/@/.test(value)) kind = "email"; // PEC se vuoi, vedi sotto
        else kind = "text";
      }

      try {
        await navigator.clipboard.writeText(value);

        const msgMap = {
          phone: "Numero di telefono copiato",
          email: "Email copiata",
          pec: "PEC copiata",
          text: "Testo copiato",
        };

        this.$toast?.success?.(`${msgMap[kind] || msgMap.text} negli appunti`);
      } catch {
        this.$toast?.error?.("Impossibile copiare negli appunti");
      }
    },

    safeDocKey(doc, i) {
      if (doc && doc.id != null) return "doc-" + String(doc.id);
      if (doc && doc.url) return "url-" + doc.url;
      if (doc && doc.name) return "name-" + doc.name;
      return "idx-" + String(i);
    },

    formatToLocalDate,
    formatToLocalDateAndHours,
    formatPriceToPrint,
    daRestituire,
    preventivoNonModificabile,

    formatDocName(name) {
      return (
        (name ?? "")
          .toString()
          // prima togli eventuale ".dev" immediatamente prima di ".pdf"
          .replace(/\.dev(?=\.pdf$)/i, "")
          // poi rimuovi "_N_<numero>.pdf" in fondo
          .replace(/_N_\d+\.pdf$/i, "")
      );
    },

    getLatestQuoteDocument(quote) {
      const documents = Array.isArray(quote?.documents)
        ? quote.documents.filter(Boolean)
        : [];

      if (!documents.length) return null;

      return [...documents].sort((a, b) => {
        const dateA = new Date(a?.createdAt || 0).getTime();
        const dateB = new Date(b?.createdAt || 0).getTime();
        return dateB - dateA;
      })[0];
    },

    hasQuoteDocument(quote) {
      return Boolean(this.getLatestQuoteDocument(quote));
    },

    getQuoteEmailSentKey(
      quote,
      selectedDoc = this.getLatestQuoteDocument(quote)
    ) {
      const quoteId = quote?.id ?? "unknown-quote";
      const contact =
        quote?.Client?.pec || quote?.Client?.email || "no-contact";
      const objectName = selectedDoc?.name || "no-document";
      return `quote-email-sent:${quoteId}:${contact}:${objectName}`;
    },

    hasSentQuoteEmail(quote) {
      const selectedDoc = this.getLatestQuoteDocument(quote);
      if (!selectedDoc || typeof window === "undefined") return false;
      return (
        window.localStorage.getItem(
          this.getQuoteEmailSentKey(quote, selectedDoc)
        ) === "1"
      );
    },

    markQuoteEmailAsSent(quote, selectedDoc) {
      if (typeof window === "undefined" || !selectedDoc) return;
      window.localStorage.setItem(
        this.getQuoteEmailSentKey(quote, selectedDoc),
        "1"
      );
    },

    async sendExistingQuoteEmail(quote) {
      const quoteId = quote?.id ?? null;
      if (!quoteId) {
        this.$toast.error("Preventivo non valido.");
        return;
      }

      const contact = quote?.Client?.pec || quote?.Client?.email || "";
      const selectedDoc = this.getLatestQuoteDocument(quote);
      const objectName = selectedDoc?.name || null;

      if (!objectName) {
        this.$toast.error(
          "Nessun PDF caricato per questo preventivo. Aprilo in modifica e salva prima di inviare."
        );
        return;
      }

      if (!contact) {
        this.$toast.warning(
          "Il cliente non ha email o PEC configurate. Invio non disponibile."
        );
        return;
      }

      try {
        this.sendingQuoteEmailId = quoteId;
        this.$toast.info("Invio email al cliente...");
        await this.sendQuoteEmail({ quoteId, objectName, contact });
        this.markQuoteEmailAsSent(quote, selectedDoc);
        this.$toast.success("Email inviata con successo!");
        await this.getQuotes(this.pagination.currentPage);
      } catch (error) {
        console.error("Errore invio email preventivo:", error);
        this.$toast.error(
          error?.response?.data?.details ||
            error?.response?.data?.error ||
            "Errore durante l'invio dell'email."
        );
      } finally {
        this.sendingQuoteEmailId = null;
      }
    },

    openDocumentsModal(quote) {
      this.currentQuoteId = quote?.id ?? null;
      this.selectedDocuments = []; // reset prima
      this.docSearchTerm = ""; // reset ricerca
      this.showDocumentsModal = true;
      this.$nextTick(() => {
        this.selectedDocuments = (quote.documents || []).filter(Boolean);
      });
    },
    closeDocumentsModal() {
      this.showDocumentsModal = false;
      this.selectedDocuments = [];
      this.currentQuoteId = null;
      this.docSearchTerm = "";
    },

    // Rigenera il PDF del preventivo corrente riusando generatePdf (rigenera
    // client-side, ri-carica sul backend con lo stesso nome file sostituendo il
    // documento, scarica in locale e ricarica la lista). La modale resta aperta:
    // dopo il refetch riallineo selectedDocuments così la card mostra la nuova data.
    async regenerateQuotePdf() {
      if (this.regeneratingPdf || this.currentQuoteId == null) return;
      this.regeneratingPdf = true;
      try {
        // Stessa strategia di applyModifier: uso la riga locale se ha i dettagli
        // (item/outstock) necessari al PDF, altrimenti fallback su getQuotebyId.
        const qLocal = this.allQuotes.find((x) => x.id === this.currentQuoteId);
        const needsFetch = !qLocal?.quote_items?.some(
          (qi) => qi?.outstock || qi?.item
        );
        const dataForPdf = needsFetch
          ? await this.getQuotebyId(this.currentQuoteId)
          : qLocal;

        await this.generatePdf(dataForPdf);

        // generatePdf ha già rifatto getQuotes: riallineo i documenti mostrati.
        const updated = this.allQuotes.find(
          (x) => x.id === this.currentQuoteId
        );
        this.selectedDocuments = (updated?.documents || []).filter(Boolean);
      } finally {
        this.regeneratingPdf = false;
      }
    },

    /* ----- ARCHIVIAZIONE / RIPRISTINO con modale ----- */
    openArchiveModal(quote) {
      this.currentQuoteId = quote?.id ?? null;
      this.selectedQuote = quote;
      this.showArchiveModal = true;
    },
    closeArchiveModal() {
      if (this.archiving) return;
      this.showArchiveModal = false;
      this.selectedQuote = null;
      this.currentQuoteId = null;
    },
    async confirmArchive() {
      if (!this.selectedQuote) return;
      try {
        this.archiving = true;
        let response;
        if (this.$store._actions?.archiveQuote) {
          response = await this.archiveQuote(this.selectedQuote.id);
        } else {
          response = await this.$axios.patch(
            `quote/archive/${this.selectedQuote.id}`,
            JSON.stringify({ archived: true })
          );
        }
        this.successMessage =
          response.data?.message || "Preventivo archiviato con successo.";
        this.$toast.success(this.successMessage);

        this.showArchiveModal = false;
        this.currentQuoteId = null;

        await this.getQuotes(this.pagination.currentPage);
      } catch (err) {
        this.errorMessage =
          err.response?.data?.details ||
          "Impossibile archiviare il preventivo.";
        this.$toast.error(this.errorMessage);
      } finally {
        this.archiving = false;
        this.selectedQuote = null;
      }
    },

    openRestoreModal(quote) {
      this.currentQuoteId = quote?.id ?? null;
      this.selectedQuote = quote;
      this.showRestoreModal = true;
    },

    // sc-48744: eliminazione dalla UI, solo dalla scheda "Archiviati"
    openDeleteModal(quote) {
      this.currentQuoteId = quote?.id ?? null;
      this.selectedQuote = quote;
      this.showDeleteModal = true;
    },

    closeDeleteModal() {
      if (this.deleting) return;
      this.showDeleteModal = false;
      this.selectedQuote = null;
      this.currentQuoteId = null;
    },

    async confirmDelete() {
      if (!this.selectedQuote) return;
      try {
        this.deleting = true;
        const response = await this.deleteQuote(this.selectedQuote.id);

        this.successMessage =
          response.data?.message || "Preventivo eliminato con successo.";
        this.$toast.success(this.successMessage);

        this.showDeleteModal = false;
        this.currentQuoteId = null;
        this.selectedQuote = null;

        await this.getQuotes(this.pagination.currentPage);
      } catch (err) {
        // 403: la rotta è riservata agli admin (sc-48744)
        this.errorMessage =
          err.response?.status === 403
            ? "Solo un amministratore può eliminare un preventivo."
            : err.response?.data?.message ||
              err.response?.data?.details ||
              "Errore durante l'eliminazione del preventivo.";
        this.$toast.error(this.errorMessage);
      } finally {
        this.deleting = false;
      }
    },
    openDiscountModal(quote) {
      this.currentQuoteId = quote?.id ?? null;
      this.selectedQuote = quote;
      this.showDiscountModal = true;
      this.docSearchTerm = ""; // reset ricerca
      this.selectedDocuments = quote.documents || [];
    },
    // Totale residuo da saldare: totale − sconto già applicato − acconto già applicato
    residualToPay(quote) {
      const total = Number(quote?.totalPrice) || 0;
      const discount =
        Number(quote?.discount) ||
        (total * (Number(quote?.discount_percent) || 0)) / 100;
      const deposit = Number(quote?.deposit) || 0;
      return Math.max(0, total - discount - deposit);
    },
    clampDiscountAmount(quote) {
      const base = Math.max(
        0,
        (Number(quote?.totalPrice) || 0) - (Number(quote?.deposit) || 0)
      );
      let v = Number(this.discountAmount);
      if (!Number.isFinite(v) || v < 0) v = 0;
      if (v > base) v = base;
      this.discountAmount = v;
    },
    clampDepositAmount(quote) {
      const base = Math.max(
        0,
        (Number(quote?.totalPrice) || 0) - (Number(quote?.discount) || 0)
      );
      let v = Number(this.depositAmount);
      if (!Number.isFinite(v) || v < 0) v = 0;
      if (v > base) v = base;
      this.depositAmount = v;
    },
    closeDiscountModal() {
      if (this.restoring) return;
      this.showDiscountModal = false;
      this.selectedQuote = null;
      this.currentQuoteId = null;
      this.modifierType = "";
      this.discountAmount = 0;
      this.depositAmount = 0;
      this.selectedDocuments = [];
      this.docSearchTerm = "";
    },
    closeRestoreModal() {
      if (this.restoring) return;
      this.showRestoreModal = false;
      this.selectedQuote = null;
      this.currentQuoteId = null;
    },
    async confirmRestore() {
      if (!this.selectedQuote) return;
      try {
        this.restoring = true;
        let response;
        if (this.$store._actions?.restoreQuote) {
          response = await this.restoreQuote(this.selectedQuote.id);
        } else {
          response = await this.$axios.patch(
            `quote/archive/${this.selectedQuote.id}`,
            JSON.stringify({ isArchived: false })
          );
        }
        this.successMessage =
          response.data?.message || "Preventivo ripristinato con successo.";
        this.$toast.success(this.successMessage);

        this.showRestoreModal = false;
        this.currentQuoteId = null;

        await this.getQuotes(this.pagination.currentPage);
      } catch (err) {
        this.errorMessage =
          err.response?.data?.message ||
          err.response?.data?.details ||
          "Impossibile ripristinare il preventivo.";
        this.$toast.error(this.errorMessage);
      } finally {
        this.restoring = false;
        this.selectedQuote = null;
      }
    },

    /* ----- Ricerca/paginazione ----- */
    async onSearch() {
      this.isLoading = true;
      try {
        this.currentPage = 1;
        await this.fetchQuotes({
          page: 1,
          limit: this.pageSize,
          search: this.searchTerm,
          tab: this.activeTab,
        });
      } catch (err) {
        const msg =
          err?.response?.data?.message ||
          err?.response?.data?.details ||
          "Nessun preventivo per la ricerca richiesta.";
        this.errorMessage = msg;
        this.$toast.error(msg);
      } finally {
        this.isLoading = false;
      }
    },

    async onResetSearch() {
      this.isLoading = true;
      try {
        this.currentPage = 1;
        this.searchTerm = "";
        await this.fetchQuotes({
          page: 1,
          limit: this.pageSize,
          tab: this.activeTab,
        });
      } catch (err) {
        const msg =
          err?.response?.data?.message ||
          err?.response?.data?.details ||
          "Errore durante il reset della ricerca.";
        this.errorMessage = msg;
        this.$toast.error(msg);
      } finally {
        this.isLoading = false;
      }
    },

    goToPage(page) {
      if (page < 1 || page > this.pagination.totalPages) return;
      this.getQuotes(page);
    },

    async getQuotes(page, search = this.searchTerm) {
      this.isLoading = true;
      try {
        const res = await this.fetchQuotes({
          page,
          limit: this.pageSize,
          search,
          tab: this.activeTab,
        });
        // se lo store ha fatto swallow del 404 e ti dà [] con search attivo
        if (Array.isArray(res) && res.length === 0 && (search || "").trim()) {
          this.errorMessage = "Nessun preventivo per la ricerca richiesta.";
          this.$toast.error(this.errorMessage);
        }
        return res;
      } catch (error) {
        if (error?.response?.status === 404 && page !== 1) {
          // prova la prima pagina
          try {
            await this.fetchQuotes({
              page: 1,
              limit: this.pageSize,
              search,
              tab: this.activeTab,
            });
            this.pagination.currentPage = 1;
            return;
          } catch (_) {
            /* ignora, gestiamo sotto */
          }
        }
        // toast + messaggio
        this.$nextTick(() => {
          const defaultMsg =
            this.searchTerm && this.searchTerm.trim()
              ? "Nessun preventivo per la ricerca richiesta."
              : this.isArchivedTab
              ? "Non esistono preventivi archiviati."
              : "Non esistono preventivi in questa scheda.";

          this.errorMessage =
            error?.response?.data?.message ||
            error?.response?.data?.details ||
            defaultMsg;

          this.$toast.error(this.errorMessage);
        });
      } finally {
        this.isLoading = false;
      }
    },

    // sc-47952: cambio scheda, si riparte da pagina 1.
    async switchTab(tab) {
      if (this.activeTab === tab) return;
      this.activeTab = tab;
      this.currentPage = 1;
      await this.getQuotes(1, this.searchTerm);
    },

    goToQuoteDetail(quoteId) {
      this.$router.push({ name: "detail-quote", params: { quoteId } });
    },

    // Calcola e disegna la tabella articoli (7 colonne)
    // buildQuoteTable(quote, pageWidth, marginX) {
    //   // intestazione fissa (cod, desc, qty, um, price, iva, tot)
    //   const head = [
    //     [
    //       "Cod.",
    //       "Descrizione",
    //       "Q.tà",
    //       "U.m.",
    //       "Prezzo ivato",
    //       "Iva",
    //       "Importo ivato",
    //     ],
    //   ];

    //   const body = [];

    //   // Mappa rapida per sapere quali items sono già stati coperti
    //   const itemMap = new Map((quote.items || []).map((it) => [it.id, it]));

    //   // 1) Righe da magazzino (quote.items + QuoteItem)
    //   (quote.items || []).forEach((it) => {
    //     const code = it.code || "-";
    //     const desc = it.itemName || it.subBrand || it.brand || "-";
    //     const nameLC = String(it.itemName || "").toLowerCase();
    //     const isTiles = it.category === "piastrelle";
    //     const isBattiscopa = nameLC.includes("battiscopa");

    //     let qty = Number(it?.QuoteItem?.quantity || 0);
    //     let unit = "pz";
    //     if (isTiles) {
    //       const sqmPerBox = Number(it.sqmPerBox || 0) || 1;
    //       qty = Number((sqmPerBox * qty).toFixed(2));
    //       unit = "mQ";
    //     } else if (isBattiscopa) {
    //       unit = "mL";
    //     }

    //     const unitPrice = Number(it?.QuoteItem?.price || it.price || 0);
    //     const lineTotal = Number((unitPrice * qty).toFixed(2));
    //     const iva = it?.iva ?? 22;

    //     body.push([
    //       code,
    //       desc,
    //       String(qty),
    //       unit,
    //       `€ ${unitPrice.toFixed(2)}`,
    //       `${iva}%`,
    //       `€ ${lineTotal.toFixed(2)}`,
    //     ]);
    //   });

    //   // 2) Righe "orfane" da quote_items (fuori magazzino ecc.)
    //   (quote.quote_items || []).forEach((qi) => {
    //     const id = qi?.item?.id;
    //     if (id && itemMap.has(id)) return; // già in body

    //     const code = qi?.item?.code ?? "-";
    //     const desc =
    //       qi?.outstock?.itemName ||
    //       qi?.outstock?.brand ||
    //       qi?.item?.itemName ||
    //       "-";
    //     const nameLC = desc.toLowerCase();
    //     const isTiles =
    //       qi?.item?.category === "piastrelle" ||
    //       qi?.outstock?.subCategory === "piastrelle";
    //     const isBattiscopa = nameLC.includes("battiscopa");

    //     const qty = Number(qi.quantity || 0);
    //     const unit = isBattiscopa ? "mL" : isTiles ? "mQ" : "pz";

    //     const unitPrice = Number(qi.price || 0);
    //     const lineTotal = Number((unitPrice * qty).toFixed(2));
    //     const iva = qi?.iva ?? 22;

    //     body.push([
    //       code,
    //       desc,
    //       String(qty),
    //       unit,
    //       `€ ${unitPrice.toFixed(2)}`,
    //       `${iva}%`,
    //       `€ ${lineTotal.toFixed(2)}`,
    //     ]);
    //   });

    //   // Larghezze: desc auto, le altre fisse
    //   const usable = pageWidth - 2 * marginX;
    //   const w = {
    //     cod: 0.1, // ~10%
    //     qty: 0.1, // ~10%
    //     um: 0.08, // ~8%
    //     price: 0.18, // ~18%
    //     iva: 0.08, // ~8%
    //     tot: 0.18, // ~18%
    //     // desc prenderà il resto in auto
    //   };
    //   const cw = {
    //     0: Math.floor(usable * w.cod), // Cod.
    //     2: Math.floor(usable * w.qty), // Q.tà
    //     3: Math.floor(usable * w.um), // U.m.
    //     4: Math.floor(usable * w.price), // Prezzo
    //     5: Math.floor(usable * w.iva), // Iva
    //     6: Math.floor(usable * w.tot), // Totale
    //   };

    //   const columnStyles = {
    //     // 0 Cod.
    //     0: { cellWidth: cw[0], halign: "left" },
    //     // 1 Desc => auto + minimo
    //     1: { cellWidth: "auto", minCellWidth: 40, halign: "left" },
    //     // 2 Qty
    //     2: { cellWidth: cw[2], halign: "right" },
    //     // 3 Um
    //     3: { cellWidth: cw[3], halign: "center" },
    //     // 4 Price
    //     4: { cellWidth: cw[4], halign: "right" },
    //     // 5 Iva
    //     5: { cellWidth: cw[5], halign: "right" },
    //     // 6 Tot
    //     6: { cellWidth: cw[6], halign: "right" },
    //   };

    //   return {
    //     head,
    //     body,
    //     tableWidth: usable,
    //     margin: { left: marginX, right: marginX },
    //     columnStyles,
    //   };
    // },

    // Calcola e disegna la tabella articoli (7 colonne) — con GRUPPI PER CATEGORIA
    buildQuoteTable(quote, pageWidth, marginX) {
      const head = [
        [
          "Cod.",
          "Descrizione",
          "Scatole",
          "Q.tà",
          "U.m.",
          "Prezzo ivato",
          "Iva",
          "Importo ivato",
        ],
      ];

      const lower = (v) => String(v || "").toLowerCase();

      // 1) Normalizzo tutte le righe in una lista unica
      const rows = [];
      const itemMap = new Map((quote.items || []).map((it) => [it.id, it]));

      // a) Righe da magazzino (quote.items + it.QuoteItem)
      (quote.items || []).forEach((it) => {
        // sc-48197: regola unica "marca - serie - colore" (prima: solo la serie,
        // perche' per i sanitari itemName e' vuoto e la catena ripiegava su subBrand).
        // Niente withMeasure: la misura non faceva parte del titolo nemmeno prima.
        // itemFullDescription aggiunge tipo/posizione ai sanitari (sc-48197).
        const name = itemFullDescription(it) || "-";
        const isTiles = lower(it.category) === "piastrelle";
        const isBattiscopa = lower(name).includes("battiscopa");

        const baseQty = Number(it?.QuoteItem?.quantity || 0);
        const sqmPerBox = Number(it.sqmPerBox || 0) || 1;
        // sc-48721: l'importo usa i mQ pieni, come totalPrice dell'API; la Q.tà
        // mostrata resta arrotondata. Altrimenti la somma della tabella non
        // torna col totale (es. 1 scatola da 1.5376 mQ).
        const qtyPiena = isTiles ? baseQty * sqmPerBox : baseQty;
        const qty = isTiles ? Number(qtyPiena.toFixed(2)) : baseQty;
        const unit = isBattiscopa ? "mL" : isTiles ? "mQ" : "pz";

        const unitPrice = Number(it?.QuoteItem?.price || it.price || 0);
        // sc-48743: l'IVA è quella della riga del preventivo; it.iva è
        // l'IVA di catalogo dell'articolo (22 di default), che può differire.
        const iva = it?.QuoteItem?.iva ?? it?.iva ?? 22;
        const lineTotal = Number((unitPrice * qtyPiena).toFixed(2));

        rows.push({
          group: String(it.category || "Generale")
            .trim()
            .toUpperCase(),
          code: it.code || "-",
          desc: name,
          boxes: isTiles ? baseQty : "",
          qty,
          unit,
          unitPrice,
          iva,
          lineTotal,
        });
      });

      // b) Righe “orfane” in quote_items (fuori magazzino o item non presenti sopra)
      (quote.quote_items || []).forEach((qi) => {
        const id = qi?.item?.id;
        if (id && itemMap.has(id)) return; // già coperto sopra

        const src = qi?.outstock || qi?.item;
        const name = src ? itemFullDescription(src) : "-";

        const isTiles =
          lower(qi?.item?.category) === "piastrelle" ||
          lower(qi?.outstock?.subCategory) === "piastrelle";
        const isBattiscopa = lower(name).includes("battiscopa");

        const qty = Number(qi.quantity || 0); // per outstock piastrelle è già in mQ
        const unit = isBattiscopa ? "mL" : isTiles ? "mQ" : "pz";

        const unitPrice = Number(qi.price || 0);
        const iva = qi?.iva ?? 22;
        const lineTotal = Number((unitPrice * qty).toFixed(2));

        const group = qi?.outstock
          ? "FUORI MAGAZZINO"
          : String(qi?.item?.category || "Generale")
              .trim()
              .toUpperCase();

        rows.push({
          group,
          code: qi?.item?.code || "-",
          desc: name,
          boxes: "",
          qty,
          unit,
          unitPrice,
          iva,
          lineTotal,
        });
      });

      // 2) Raggruppo preservando l'ordine di apparizione
      const order = [];
      const groups = {};
      rows.forEach((r) => {
        if (!groups[r.group]) {
          groups[r.group] = [];
          order.push(r.group);
        }
        groups[r.group].push(r);
      });

      // 3) Costruisco il body con header di sezione (colspan 7)
      const body = [];
      order.forEach((g) => {
        body.push([
          {
            content: g,
            colSpan: 8,
            styles: {
              fillColor: [235, 235, 235],
              fontStyle: "bold",
              halign: "left",
            },
          },
        ]);

        groups[g].forEach((r) => {
          body.push([
            r.code,
            r.desc,
            r.boxes === "" ? "" : String(r.boxes),
            String(r.qty),
            r.unit,
            `€ ${r.unitPrice.toFixed(2)}`,
            `${r.iva}%`,
            `€ ${r.lineTotal.toFixed(2)}`,
          ]);
        });
      });

      // 4) Larghezze/colonne (descrizione auto)
      const usable = pageWidth - 2 * marginX;
      const w = {
        cod: 0.1,
        box: 0.11,
        qty: 0.1,
        um: 0.08,
        price: 0.16,
        iva: 0.08,
        tot: 0.17, // sc-48721: con Descrizione a min 40 mm la tabella resta di 190 mm
      };
      const cw = {
        0: Math.floor(usable * w.cod),
        2: Math.floor(usable * w.box),
        3: Math.floor(usable * w.qty),
        4: Math.floor(usable * w.um),
        5: Math.floor(usable * w.price),
        6: Math.floor(usable * w.iva),
        7: Math.floor(usable * w.tot),
      };

      return {
        // pdfSafeRows: descrizioni e codici arrivano dal DB, dove nessun livello
        // applicativo filtra i caratteri fuori cp1252 (cfr. utilities/pdfText.js)
        head: pdfSafeRows(head),
        body: pdfSafeRows(body),
        imponibile: totaliImponibile(
          rows.map((r) => ({ importo: r.lineTotal, iva: r.iva }))
        ),
        tableWidth: usable,
        margin: { left: marginX, right: marginX },
        columnStyles: {
          0: { cellWidth: cw[0], halign: "left" },
          1: { cellWidth: "auto", minCellWidth: 40, halign: "left" },
          2: { cellWidth: cw[2], halign: "right" },
          3: { cellWidth: cw[3], halign: "right" },
          4: { cellWidth: cw[4], halign: "center" },
          5: { cellWidth: cw[5], halign: "right" },
          6: { cellWidth: cw[6], halign: "right" },
          7: { cellWidth: cw[7], halign: "right" },
        },
      };
    },

    async generatePdf(quote) {
      // console.log("vengo richiamata?");
      try {
        const doc = new jsPDF({ unit: "mm", format: "a4" });
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const marginX = 10;
        const marginY = 10;
        let y = marginY;

        // ---- HEADER (logo + intestazione fissa)
        if (logoPath) {
          try {
            doc.addImage(logoPath, "PNG", marginX, y, 30, 30);
            // eslint-disable-next-line no-empty
          } catch {}
        }
        doc.setFontSize(9).setFont("helvetica", "normal");
        [
          "VIA SIRACUSA 83 - 96012  AVOLA  (SR)  - ITALIA ",
          "Tel. 3492253528 / 0931822066",
          "Email: emmamaison@libero.it",
        ].forEach((text, i) => {
          const x = pageWidth - marginX - doc.getTextWidth(text);
          doc.text(text, x, y + 5 + i * 4);
        });
        y += 48;

        // helper stampa "Label: Valore"
        const printLabelValue = (label, value, xPos, yPos, maxWidth = null) => {
          doc.setFont("helvetica", "bolditalic");
          doc.text(`${label}:`, xPos, yPos);
          const off = doc.getTextWidth(`${label}: `) + 0.8;
          doc.setFont("helvetica", "normal");
          // pdfSafe: i dati cliente arrivano dal DB senza sanificazione lato API
          const val = pdfSafe(value, { fallback: "N/D" });
          if (maxWidth) {
            const wrapped = doc.splitTextToSize(val, maxWidth - off);
            wrapped.forEach((ln, idx) =>
              doc.text(ln, xPos + off, yPos + idx * 5)
            );
            return yPos + wrapped.length * 5;
          } else {
            doc.text(val, xPos + off, yPos);
            return yPos + 5;
          }
        };

        // ---- DETTAGLI CLIENTE (SX) + PREVENTIVO (DX)
        const contactValue = quote?.Client?.pec || quote?.Client?.email || "";
        const contactLabel = quote?.Client?.pec ? "pec" : "email";
        const lh = (() => {
          try {
            return JSON.parse(quote?.Client?.legalHeadquarters || "{}");
          } catch {
            return {};
          }
        })();
        let leftX = marginX;
        doc
          .setFontSize(14)
          .setFont("helvetica", "bold")
          .text("Dettaglio cliente", leftX, y);
        let yLeft = y + 8;
        doc.setFontSize(11);
        yLeft = printLabelValue(
          "cliente",
          quote?.Client?.clientName,
          leftX,
          yLeft
        );
        yLeft = printLabelValue(
          "telefono",
          quote?.Client?.phoneNumber,
          leftX,
          yLeft
        );
        yLeft = printLabelValue(contactLabel, contactValue, leftX, yLeft);
        yLeft = printLabelValue(
          "partita iva",
          quote?.Client?.vatNumber,
          leftX,
          yLeft
        );
        yLeft = printLabelValue(
          "codice fiscale",
          quote?.Client?.taxCode,
          leftX,
          yLeft
        );
        yLeft = printLabelValue(
          "azienda",
          quote?.Client?.companyName,
          leftX,
          yLeft
        );
        yLeft = printLabelValue(
          "sede legale",
          lh.address,
          leftX,
          yLeft,
          pageWidth / 2 - 20
        );

        const rightX = pageWidth / 2 + 10;
        const colW = pageWidth - rightX - marginX;
        const formattedDate = new Date(quote.date).toLocaleDateString("it-IT", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        });

        doc
          .setFontSize(14)
          .setFont("helvetica", "bold")
          .text("Dettaglio preventivo", rightX, y);
        let yRight = y + 8;
        doc.setFontSize(11);
        yRight = printLabelValue("data", formattedDate, rightX, yRight);
        yRight = printLabelValue(
          "indirizzo di consegna",
          quote?.location?.address,
          rightX,
          yRight,
          colW
        );
        if (quote?.location?.link) {
          doc.setFont("helvetica", "bolditalic");
          doc.text("maps:", rightX, yRight);
          const off = doc.getTextWidth("maps: ") + 0.8;
          doc
            .setFont("helvetica", "bold")
            .setFontSize(10)
            .setTextColor(0, 0, 255);
          const safeLink = pdfSafe(quote.location.link);
          const disp =
            doc.getTextWidth(safeLink) > colW - off
              ? safeLink.slice(8, 27) + "…"
              : safeLink;
          doc.textWithLink(disp, rightX + off, yRight, {
            url: quote.location.link,
          });
          doc.setTextColor(0).setFontSize(11).setFont("helvetica", "normal");
          yRight += 5;
        }

        y = Math.max(yLeft, yRight) + 4;
        doc.setDrawColor(150).line(marginX, y, pageWidth - marginX, y);
        y += 6;

        // ---- TABELLA ARTICOLI (helper esterno)
        const tbl = this.buildQuoteTable(quote, pageWidth, marginX);
        autoTable(doc, {
          startY: y,
          theme: "grid",
          styles: { fontSize: 10, cellPadding: 1.5 },
          headStyles: { fillColor: [200, 200, 200], fontStyle: "bold" },
          head: tbl.head,
          body: tbl.body,
          columnStyles: tbl.columnStyles,
          tableWidth: tbl.tableWidth,
          margin: tbl.margin,
        });
        y = (doc.lastAutoTable?.finalY || y) + 10;

        // ---- RIEPILOGO come un conto (sc-48721)
        const minVisualRoom = 55; // margine di sicurezza per il blocco
        if (pageHeight - y < minVisualRoom) {
          doc.addPage();
          y = marginY;
        }
        y = disegnaRiquadroRiepilogo(doc, {
          y,
          pageWidth,
          marginX,
          righe: righeRiepilogo(calcolaImporti(quote), tbl.imponibile),
        });

        // ---- Upload + salvataggio locale
        const filenameBase = `Preventivo_N_${quote.id}.pdf`;
        const isDev =
          process.env.NODE_ENV === "development" ||
          import.meta?.env?.MODE === "development" ||
          process.env?.VUE_APP_NODE_ENV === "development";

        // helper FE (senza 'path')
        const withSuffixBeforeExt = (filename, suffix) => {
          if (!suffix) return filename;
          const dot = filename.lastIndexOf(".");
          if (dot <= 0) return `${filename}.${suffix}`;
          return `${filename.slice(0, dot)}.${suffix}${filename.slice(dot)}`;
        };

        const finalName = withSuffixBeforeExt(filenameBase, isDev ? "dev" : "");
        const blob = doc.output("blob");
        // 👇 crea un "File" con nome giusto, così Multer lo vede come originalname
        const file = new File([blob], finalName, { type: "application/pdf" });

        try {
          await this.$store.dispatch("uploadQuoteDocument", {
            quoteId: quote.id,
            document: file,
            contact: contactValue,
            filename: finalName,
          });
          this.$nextTick(() => {
            this.successMessage = "PDF generato e caricato con successo!";
            this.$toast.success(this.successMessage);
          });
          doc.save(finalName);
        } catch (error) {
          console.error("Errore durante l'upload del PDF:", error);
          this.$nextTick(() => {
            this.errorMessage =
              error.response?.data?.details ||
              error.response?.data?.error ||
              "Errore durante il caricamento del PDF.";
            this.$toast.error(this.errorMessage);
          });
          this.successMessage = "";
          return; // evita reset/redirect se upload fallisce
        }
        // aggiorna lista (così rivedi il nuovo documento nel modale)
        await this.getQuotes(this.pagination.currentPage);
      } catch (error) {
        this.$toast.error(
          error?.response?.data?.details ||
            error?.response?.data?.error ||
            "Errore durante la generazione/caricamento del PDF."
        );
      }
    },

    async applyModifier(quote) {
      const round2 = (n) =>
        Math.round((Number(n) + Number.EPSILON) * 100) / 100;
      if (!this.modifierType)
        return this.$toast.error("Seleziona Sconto o Acconto");

      this.confirmBusy = true;
      try {
        if (this.modifierType === "discount_percent") {
          const base = Number(quote.totalPrice) || 0;
          const deposit = Number(quote.deposit) || 0;
          const taxable = Math.max(0, base - deposit); // base scontabile
          const amount = round2(Number(this.discountAmount || 0)); // importo digitato
          if (!(amount > 0 && amount <= taxable)) {
            this.confirmBusy = false;
            return this.$toast.error(
              "Sconto non valido (deve essere tra 0 e il totale meno l'acconto)."
            );
          }

          // percent ricavato lato client solo per coerenza UI immediata
          const percent =
            taxable > 0 ? Math.round((amount / taxable) * 100) : 0;

          const { data } = await axiosInstance.post(
            `quote/priceAdjustment/${quote.id}`,
            { type: "discount_percent", amount }
          );

          // aggiorna UI locale
          const q = this.allQuotes.find((x) => x.id === quote.id);
          if (q) {
            q.discount_percent = percent;
            q.discount = amount; // sconto su (totale - acconto)
          }

          this.$toast.success(data?.message || "Sconto aggiornato.");
          this.discountAmount = 0;
        } else if (this.modifierType === "deposit") {
          const amount = Number(this.depositAmount || 0);
          const maxDeposit = Math.max(
            0,
            (Number(quote.totalPrice) || 0) - (Number(quote.discount) || 0)
          );
          if (!(amount > 0 && amount <= maxDeposit)) {
            this.confirmBusy = false;
            return this.$toast.error(
              "Acconto non valido (deve essere tra 0 e il totale meno lo sconto)."
            );
          }

          const { data } = await axiosInstance.post(
            `quote/priceAdjustment/${quote.id}`,
            { type: "deposit", amount }
          );

          // ✅ aggiorna la riga locale in lista
          const q = this.allQuotes.find((x) => x.id === quote.id);
          if (q) q.deposit = amount;

          this.$toast.success(data?.message || "Acconto aggiornato.");
          this.depositAmount = 0;
        }

        // azzera scelta radio
        this.modifierType = "";

        // ✅ usa la riga locale aggiornata per il PDF (se la lista include outstock)
        const qLocal = this.allQuotes.find((x) => x.id === quote.id);

        // fallback: se dalla lista NON arrivano i dettagli outstock/item, prendo la quote completa byId
        const needsFetch = !qLocal?.quote_items?.some(
          (qi) => qi?.outstock || qi?.item
        );
        const dataForPdf = needsFetch
          ? await this.getQuotebyId(quote.id)
          : qLocal;

        await this.generatePdf(dataForPdf);
        this.closeDiscountModal();
      } catch (e) {
        this.$toast.error(
          e?.response?.data?.message || "Errore nell’applicazione."
        );
      } finally {
        this.confirmBusy = false;
      }
    },

    async getQuotebyId(quoteId) {
      try {
        const response = await axiosInstance.get(`/quote/${quoteId}`);
        // console.log("quote by id", response);
        if (response.status === 200) return response.data.quote;
        throw new Error("Errore durante il recupero del preventivo.");
      } catch (error) {
        this.errorMessage =
          error.response?.data?.message ||
          "Impossibile caricare i dati del preventivo.";
        this.$toast.error(this.errorMessage);
      }
    },

    onQuoteStateChange(quote, field, newValue) {
      // salva cosa stai cambiando
      this.pendingChange = { id: quote.id, field, newValue };

      const qid = quote?.id ?? "—"; // 👈 ID del preventivo
      const clientName = quote?.Client?.clientName ?? "cliente";

      if (field === "isConfirmed" && newValue === true) {
        this.confirmTitle = "Conferma preventivo";
        this.confirmMessage =
          `Confermare il preventivo <strong>#${qid}</strong> di ` +
          `<strong>${clientName}</strong>?`;
        this.confirmLabel = "Conferma";
        this.confirmBusyLabel = "Confermo…";
      } else if (field === "payment_state" && newValue === "Pagato") {
        const warn =
          quote.state === "Consegnato"
            ? " Confermando <strong>Pagato</strong> non potrai più modificarlo e verrà archiviato dagli attivi."
            : "";
        this.confirmTitle = "Conferma pagamento";
        this.confirmMessage =
          `Impostare il pagamento su <strong>Pagato</strong> per il preventivo ` +
          `<strong>#${qid}</strong> di <strong>${clientName}</strong>?${warn}`;
        this.confirmLabel = "Stato => Pagato";
        this.confirmBusyLabel = "Imposto…";
      } else if (field === "state" && newValue === "Consegnato") {
        const warn =
          quote.payment_state === "Pagato"
            ? " Confermando <strong>Consegnato</strong> non potrai più modificarlo e verrà archiviato dagli attivi."
            : "";
        this.confirmTitle = "Conferma consegna";
        this.confirmMessage =
          `Segnare come <strong>Consegnato</strong> il preventivo ` +
          `<strong>#${qid}</strong> di <strong>${clientName}</strong>?${warn}`;
        this.confirmLabel = "Stato => Consegnato";
        this.confirmBusyLabel = "Aggiorno…";
      } else {
        this.confirmTitle = "Conferma modifica";
        this.confirmMessage =
          `Confermare la modifica dello stato per il preventivo ` +
          `<strong>#${qid}</strong> di <strong>${clientName}</strong>?`;
        this.confirmLabel = "Conferma";
        this.confirmBusyLabel = "Aggiorno…";
      }

      this.showConfirmModal = true;
    },

    handleConfirmCancel() {
      // Ripristina i dati UI ricaricando la pagina corrente (più semplice e sicuro)
      this.showConfirmModal = false;
      this.pendingChange = null;
      this.getQuotes(this.pagination.currentPage);
    },

    async handleConfirmOK() {
      if (!this.pendingChange) return;
      const { id, newValue } = this.pendingChange;

      this.confirmBusy = true;
      try {
        await this.updateQuoteState(id, newValue);
      } finally {
        this.confirmBusy = false;
        this.showConfirmModal = false;
        this.pendingChange = null;
      }
    },

    async updateQuoteState(quoteId, newState) {
      this.successMessage = "";
      this.errorMessage = "";
      // const quote = await this.getQuotebyId(quoteId);

      try {
        // if (quote.payment_state === "Pagato" && newState === "Consegnato") {
        //   const ok = window.confirm(
        //     "Il preventivo è già stato pagato. Confermando 'Consegnato' non potrai più modificarlo e sparirà dagli attivi. Procedere?"
        //   );
        //   if (!ok) return this.getQuotes();
        // }
        // if (quote.state === "Consegnato" && newState === "Pagato") {
        //   const ok = window.confirm(
        //     "Il preventivo è già stato consegnato. Confermando 'Pagato' non potrai più modificarlo e sparirà dagli attivi. Procedere?"
        //   );
        //   if (!ok) return this.getQuotes();
        // }

        // const ok = window.confirm(
        //   "Sei sicuro di voler confermare questo preventivo?"
        // );
        // if (!ok) return this.getQuotes();

        const payload = JSON.stringify({ state: newState });
        const response = await this.$axios.patch(
          `quote/state/${quoteId}`,
          payload
        );

        if (response.status === 200 || response.status === 201) {
          if (newState === true) {
            this.successMessage =
              response.data.message ||
              "Il preventivo è stato confermato con successo.";
            this.$toast.success(this.successMessage);
          } else if (newState === "Consegnato") {
            this.successMessage =
              response.data.message || "Stato aggiornato a 'Consegnato'.";
            this.$toast.success(this.successMessage);
          } else if (newState === "Pagato") {
            this.successMessage =
              response.data.message || "Stato aggiornato a 'Pagato'.";
            this.$toast.success(this.successMessage);
          }

          // sc-47952: un cambio di stato può spostare il preventivo di scheda
          // (es. da "Da confermare" a "Confermati"): ricarico per riflettere la
          // nuova collocazione e aggiornare i contatori delle schede.
          await this.getQuotes(this.pagination.currentPage);
        }
      } catch (error) {
        this.errorMessage =
          error.response?.status === 409
            ? error.response.data?.message ||
              error.response.data?.details ||
              `Lo stato del preventivo con ID '${quoteId}' è già '${newState}'`
            : error.response?.data?.details ||
              error.response?.data?.error ||
              "Errore durante l'aggiornamento.";
        this.$toast.error(this.errorMessage);
        this.getQuotes();
      }
    },

    /* ---- utilità stato preventivo ---- */
    isCompleted(quote) {
      // consideriamo "completato" quando è Pagato + Consegnato
      return quote?.payment_state === "Pagato" && quote?.state === "Consegnato";
    },

    canRestore(quote) {
      // negli archiviati non mostriamo il ripristino se è già completato
      return !this.isCompleted(quote);
    },

    canEdit(quote) {
      // se sto guardando gli archiviati e il preventivo è completato => niente modifica
      return !(
        this.isArchivedTab &&
        (this.canRestore(quote) || this.isCompleted(quote))
      );
    },
  },

  watch: {
    $route(to, from) {
      const toName = to?.name;
      const fromName = from?.name; // può essere undefined al primo giro
      if (toName === "quotes" && fromName !== "quotes") {
        this.getQuotes(this.currentPage);
      }
    },
    // "$route.name"(toName) {
    //   if (toName === "quotes") this.getQuotes(this.currentPage);
    // },
  },

  mounted() {
    this.getQuotes(this.currentPage).then(() => this.fitPageSize());

    document.addEventListener("click", this.handleDocumentClick, {
      passive: true,
    });
  },

  beforeUnmount() {
    document.removeEventListener("click", this.handleDocumentClick);
  },
};
</script>

<style src="@/css/QuotePage.css"></style>
